/**
 * 筹码大师工具函数
 */
import moment from 'moment'

// 筹码状态枚举
export const ChipState = {
    STABLE: '筹码稳定',
    CONCENTRATED: '筹码集中',
    LOOSE: '筹码松动',
    DISORDERED: '筹码无序'
} as const

// 筹码形态枚举
export const ChipShape = {
    DOUBLE_PEAK: '双峰密集',
    HIGH_CONCENTRATED: '高位密集',
    HIGH_OSCILLATION: '高位震荡',
    LOW_LOCKED: '低位锁定',
    LOW_CONCENTRATED: '低位密集',
    LOW_OSCILLATION: '低位震荡',
    OTHER: '其他形态'
} as const

// 筹码状态颜色配置
export const ChipStateColors = {
    light: {
        stable: 'rgba(245, 88, 18, 1)',    // 筹码稳定 - 橙色
        concentrated: '#cc2b1d',            // 筹码集中 - 红色
        loose: '#439731',                   // 筹码松动 - 绿色
        disordered: '#4D4D4D'               // 筹码无序/--- - 代码列颜色
    },
    dark: {
        stable: 'rgba(245, 88, 18, 1)',    // 筹码稳定 - 橙色
        concentrated: '#ff5c5c',            // 筹码集中 - 红色
        loose: '#39e365',                   // 筹码松动 - 绿色
        disordered: '#DFE2EA'               // 筹码无序/--- - 代码列颜色
    }
}

/**
 * 格式化入选时间为 yyyy-mm-dd 格式
 * @param timestamp 时间戳（毫秒）
 * @returns 格式化后的日期字符串
 */
export function formatInPoolTime(timestamp: number | undefined | null): string {
    if (!timestamp) return '--'
    return moment(timestamp).format('YYYY-MM-DD')
}

/**
 * 格式化筹码大师更新时间
 * 当日数据未出：显示上一个交易日日期（yyyy-mm-dd）
 * 当日数据已出：显示当日时分（hh:mm）
 * @param updateTime 更新时间戳（毫秒）
 * @param tradingDay 交易日时间戳（毫秒）
 * @returns 格式化后的时间字符串
 */
export function formatChipMasterUpdateTime(updateTime: number | undefined | null, tradingDay?: number | undefined | null): string {
    if (!updateTime) return '--'

    const updateDate = moment(updateTime)
    const today = moment().startOf('day')
    const updateDateStart = updateDate.clone().startOf('day')

    // 判断更新时间是否是今天
    if (updateDateStart.isSame(today)) {
        // 当日数据已出，显示时分
        return updateDate.format('HH:mm')
    } else {
        // 当日数据未出，显示交易日日期
        if (tradingDay) {
            return moment(tradingDay).format('YYYY-MM-DD')
        }
        return updateDate.format('YYYY-MM-DD')
    }
}

/**
 * 根据筹码状态获取颜色
 * @param state 筹码状态文本
 * @param stateColor 状态颜色代码（1=橙色/稳定, 2=红色/集中, 3=绿色/松动）
 * @param theme 主题 'light' | 'dark'
 * @returns 颜色值
 */
export function getChipStateColor(state: string, stateColor?: number, theme: 'light' | 'dark' = 'light'): string {
    const colors = ChipStateColors[theme]

    // 优先使用 stateColor 字段
    if (stateColor !== undefined && stateColor !== null) {
        switch (stateColor) {
            case 1: return colors.stable      // 橙色 - 筹码稳定
            case 2: return colors.concentrated // 红色 - 筹码集中
            case 3: return colors.loose       // 绿色 - 筹码松动
            default: return colors.disordered // 灰色 - 筹码无序
        }
    }

    // 根据状态文本判断
    switch (state) {
        case ChipState.STABLE:
            return colors.stable
        case ChipState.CONCENTRATED:
            return colors.concentrated
        case ChipState.LOOSE:
            return colors.loose
        case ChipState.DISORDERED:
        default:
            return colors.disordered
    }
}

/**
 * 获取筹码形态颜色（使用代码列颜色，和四维猎手的代码列一致）
 * @param theme 主题 'light' | 'dark'
 * @returns 颜色值
 */
export function getChipShapeColor(theme: 'light' | 'dark' = 'light'): string {
    return theme === 'dark' ? '#DFE2EA' : '#4D4D4D'
}

/**
 * 获取入选时间颜色（使用代码列颜色，和四维猎手的代码列一致）
 * @param theme 主题 'light' | 'dark'
 * @returns 颜色值
 */
export function getInPoolTimeColor(theme: 'light' | 'dark' = 'light'): string {
    return theme === 'dark' ? '#DFE2EA' : '#4D4D4D'
}

/**
 * 验证筹码状态是否有效
 * @param state 筹码状态
 * @returns 是否有效
 */
export function isValidChipState(state: string): boolean {
    return Object.values(ChipState).includes(state as any)
}

/**
 * 验证筹码形态是否有效
 * @param shape 筹码形态
 * @returns 是否有效
 */
export function isValidChipShape(shape: string): boolean {
    return Object.values(ChipShape).includes(shape as any)
}

export default {
    ChipState,
    ChipShape,
    ChipStateColors,
    formatInPoolTime,
    formatChipMasterUpdateTime,
    getChipStateColor,
    getChipShapeColor,
    getInPoolTimeColor,
    isValidChipState,
    isValidChipShape
}
